import dayjs from 'dayjs';
import type { RateInputs, RateApiResponse, RateData } from '../types/rates';
import { validationValues } from '../config/formConfig';

// Get rates for a specific investment date
export async function getRates(investmentDate: string ): Promise<RateApiResponse> {
  // Validate date format
  const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
  if (!dateRegex.test(investmentDate)) {
    throw new Error('Investment date must be in YYYY-MM-DD format');
  }

  const date = dayjs(investmentDate);
  const minDate = dayjs(validationValues.investmentDateMin);
  // const today = dayjs(validationValues.investmentDateMax);

  // Validate date range
  if (date.isBefore(minDate)) {
    throw new Error(`Investment date must be on or after ${validationValues.investmentDateMin}`);
  }
  // if (date.isAfter(today)) {
  //   throw new Error('Investment date cannot be in the future');
  // }

  // Get available rate files
  const rateFiles = await getAvailableRateFiles();
  
  // Find the appropriate period (on or before investment date)
  const periodFile = findRateFile(rateFiles, investmentDate);
  if (!periodFile) {
    throw new Error('No rate data available for the specified date');
  }

  // Find next period (after investment date)
  const nextPeriodFile = findNextRateFile(rateFiles, investmentDate);

  // Load rate data
  const period = await loadRateFile(periodFile);
  const nextPeriod = nextPeriodFile ?  await loadRateFile(nextPeriodFile) : null;
  
  return {
    requestedAt: dayjs().toISOString(),
    data: {
      period,
      nextPeriod
    }
  };
}

// Get list of available rate files
async function getAvailableRateFiles(): Promise<string[]> {
  // In a real implementation, this would scan the directory or call an API
  // For now, we'll return a hardcoded list based on what we know exists
  return [
    '20230301', '20230401', '20230501', '20230601', '20230701', '20230801', '20230901',
    '20231001', '20231101', '20231201', '20240101', '20240201', '20240301', '20240401',
    '20240501', '20240514', '20240601', '20240701', '20240801', '20240901', '20241001',
    '20241101', '20241201', '20250101', '20250201', '20250301', '20250401', '20250501',
    '20250601', '20250701', '20250801', '20250901', '20251001', '20251101', '20251201',
    '20260101', '20260119', '20260301'
  ];
}

// Find the appropriate rate file for a given date (on or before)
function findRateFile(rateFiles: string[], investmentDate: string): string | null {
  const targetDate = investmentDate.replace(/-/g, '');
  
  // Sort files and find the latest one that's on or before the target date
  const sortedFiles = rateFiles.sort();
  let selectedFile: string | null = null;
  
  for (const file of sortedFiles) {
    if (file <= targetDate) {
      selectedFile = file;
    } else {
      break;
    }
  }
  
  return selectedFile;
}

// Find the next rate file after a given date
function findNextRateFile(rateFiles: string[], investmentDate: string): string | null {
  const targetDate = investmentDate.replace(/-/g, '');
  
  // Sort files and find the first one that's after the target date
  const sortedFiles = rateFiles.sort();
  
  for (const file of sortedFiles) {
    if (file > targetDate) {
      return file;
    }
  }
  
  return null;
}

// Load rate data from a file
async function loadRateFile(filename: string): Promise<RateData> {
  try {
    // Import the JSON file dynamically
    const module = await import(`../data/${filename}_rates.json`);
    return module.default;
  } catch (error) {
    throw new Error(`Error loading rate data for ${filename}: ${error}`);
  }
}

// Helper function to determine rate type key
function getRateTypeKey(inputs: RateInputs): string {
  const casPrefix = inputs.agePensionPlus ? 'CAS' : 'nonCAS';
  const spousePrefix = inputs.hasSpouse ? 'Spouse' : 'nonSpouse';
  const paymentType = inputs.paymentType === 'fixed' ? 'Fixed' : 'Rising';
  

  
  return `${casPrefix}${spousePrefix}_${paymentType}`;
}

// Sub-function to lookup rates for a specific period
function lookupRatesForPeriod(inputs: RateInputs, rateData: RateData): { effectiveFrom: string; baseRate: number; escalatorRate: number; lifetimeIncomeRate: number } {
  // Determine gender and age to use
  // For spouse scenarios, use the younger age
  let spouseToUse: 'lifeInsured' | 'spouse' = 'lifeInsured'

  if (inputs.hasSpouse && inputs.spouseAge) {
    if (inputs.lifeInsuredAge === inputs.spouseAge) {
      spouseToUse = inputs.youngerSpouse || 'lifeInsured'
    } else {
      spouseToUse = inputs.lifeInsuredAge <= inputs.spouseAge ? 'lifeInsured' : 'spouse'
    }
  }

  const ageKey = spouseToUse === 'lifeInsured' ? inputs.lifeInsuredAge.toString() : (inputs.spouseAge ?? inputs.lifeInsuredAge).toString();
  const rateTypeBase = `${getRateTypeKey(inputs)}_Base`;
  const rateTypeEscalator = `${getRateTypeKey(inputs)}_Escalator`;
  const genderValue = inputs[spouseToUse === 'lifeInsured' ? 'lifeInsuredGender' : 'spouseGender'];
  const gender = genderValue === 'male' ? 'Male' : 'Female';

  // Get rates from data
  const baseRate = rateData[gender]?.[rateTypeBase]?.[ageKey];
  const escalatorRate = rateData[gender]?.[rateTypeEscalator]?.[ageKey];

  if (baseRate === undefined || escalatorRate === undefined) {
    console.error('Rate lookup failed - available data structure:');
    console.error('Available genders:', Object.keys(rateData));
    if (rateData[gender]) {
      console.error(`Available rate types for ${gender}:`, Object.keys(rateData[gender]));
      if (rateData[gender][rateTypeBase]) {
        console.error(`Available ages for ${rateTypeBase}:`, Object.keys(rateData[gender][rateTypeBase]));
      }
    }
    throw new Error('No rate data available for the specified parameters');
  }

  // Calculate lifetime income rate (base + escalator * deferral period)
  const lifetimeIncomeRate = baseRate + (escalatorRate * inputs.deferralPeriod);

  return {
    effectiveFrom: rateData.effectiveFrom,
    baseRate,
    escalatorRate,
    lifetimeIncomeRate
  };
}

// Lookup rates using the inputs
// testOnly flag is used for testing as we never need the next Period
export async function lookupRates(inputs: RateInputs, testOnly = false) {
  // Validate Investment Date
  if (!inputs.investmentDate) {
    throw new Error('Ensure a valid Investment Date is selected');
  }

  // Get rate data for the investment date
  const includeNextPeriod = !testOnly && inputs.investorType === 'new'
  const rateResponse = await getRates(inputs.investmentDate);

  const res = {
    period: lookupRatesForPeriod(inputs, rateResponse.data.period),
    nextPeriod: (includeNextPeriod && rateResponse.data.nextPeriod) ? lookupRatesForPeriod(inputs, rateResponse.data.nextPeriod) : undefined,
    nextPeriodEffectiveDate: (!includeNextPeriod && rateResponse.data.nextPeriod) ? rateResponse.data.nextPeriod.effectiveFrom : undefined,
  }

    // Debug: Log the response from getRates
  if (!testOnly && (import.meta as { env?: Record<string, string> }).env?.VITE_DEBUG_LOGGING === 'true') {
    console.log('==== INPUT DATA ====');
    console.log(inputs, null, 2)
    
    console.log('==== OUTPUT DATA ====');
    console.log(res, null, 2)
  }

  return res
}